import { Link } from '@remix-run/react';
import styled, { css } from 'styled-components';
import { ScratchHeroAnchor } from '~/common/constants/scratchDesigns';
import { breakpoints } from '~/theme/breakpoints';
import {
  CountdownTitle,
  TimeLabel,
  TimeNumber,
  TimeSeparator,
} from '~/components/countdown';
import { fontFamilies } from '~/theme/typography';

/**
 * MUST stay `.tsx`. `styled-components-esbuild-plugin.js` filters its `onLoad` hook on
 * `/\.[tj]sx$/`, so in a `.ts` file `babel-plugin-styled-components` never runs, there is no
 * deterministic `componentId`, and class names fall back to a runtime counter that differs
 * between the worker bundle and the client chunk. This app server-renders no
 * styled-components CSS, so the mismatch renders the element with DEFAULT BROWSER STYLING
 * and no error anywhere. See CLAUDE.md §3.
 */

/**
 * The camo background is painted by the `.scratch-camo` class in `app/styles/app.css`, not
 * here, and the per-design image URLs arrive as inline CSS custom properties.
 *
 * That is not a style preference. Three constraints force it:
 *   1. this app server-renders no styled-components CSS, so a `background-image` written in
 *      a `styled()` block is undiscoverable until hydration — and this is the LCP element;
 *   2. Remix v1's `links` export is static per route and cannot read loader data, so it
 *      cannot know which of the eight camos to preload;
 *   3. `app.css` is already a render-blocking stylesheet on every page.
 * A static rule plus inline custom properties is the only combination that paints before
 * hydration while keeping the content-hashed asset URLs.
 */
export const ScratchSection = styled.section<{ $hasCountdown: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100svh;

  /*
   * The countdown gets the same black outline as the footer lines, because it sits on the
   * same busy camo and washes out against the lighter patches without one.
   *
   * Scoped to this section with component selectors rather than applied in
   * app/components/countdown, which is shared presentation: that module also draws the
   * timer on /signup, /private-sale, /private-sale-sign-up and /shop's sign-up takeover,
   * none of which asked for an outline and all of which sit on different backgrounds.
   *
   * paint-order is the load-bearing half, and it is what makes this survivable on the
   * 8px "Days"/"Hrs" labels: webkit-text-stroke alone centres the stroke on the glyph edge,
   * so half of a 2px stroke would eat inwards and close their counters. Painting the stroke
   * first and the fill over it keeps every glyph at full weight with the 2px outside.
   */
  ${CountdownTitle}, ${TimeNumber}, ${TimeSeparator}, ${TimeLabel} {
    paint-order: stroke fill;
    -webkit-text-stroke: 2px #000000;
  }

  /*
   * Header clearance for the countdown, matching CampaignScreen.Container exactly.
   *
   * countdownPositioning is position:fixed only ABOVE 1280px; at and below lg it
   * becomes position:static and joins the flow -- at which point the PAGE owns the
   * offset, not the component (see the docblock on PageCountdown). Without this the
   * countdown renders underneath the site header on tablet and mobile.
   *
   * Gated on $hasCountdown for the same reason CampaignScreen gates it: the clearance
   * exists FOR the countdown, so an unconfigured one must not leave a gap behind.
   */
  @media ${({ theme }) => theme.breakpoints.lg} {
    padding-top: ${({ $hasCountdown }) => ($hasCountdown ? '4.5rem' : '0')};
  }
`;

/**
 * The card + footer column.
 *
 * Top-aligned with an explicit offset rather than vertically centred. In Figma the card
 * starts 234px down a 852px frame (27.5%), so the offset is expressed in svh to track the
 * same proportion.
 *
 * This drives the hero artwork too, indirectly: the graphics are anchored to the card's own
 * corners, so wherever the card sits, they follow. Centring the card vertically pushed the
 * whole composition down into the product grid.
 *
 * The offset shrinks below lg because the countdown stops being position:fixed there and
 * takes flow space of its own, on top of the section padding that clears the header.
 */
export const ScratchInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  width: 100%;
  padding: 0 2.5rem 2.5rem;

  /*
   * The design proportion, or whatever is left after the card and the footer, whichever
   * is SMALLER -- then floored so the hero art cannot leave the screen.
   *
   * 592px = the 492px card (106px chrome + two 193px rows, see ScratchScroll) plus the
   * 100px footer block (two 8px gaps, two 1.2 line-height Anton lines, 2.5rem padding).
   * The card is a constant here because above 1280px the 49.5rem cap always wins over
   * the gutter term, so the desktop card is always exactly the Figma 792x492.
   *
   * At the 852px design height this resolves to 234px, which is where Figma puts the
   * card top. It only starts trimming on shorter viewports, and the card stays at design
   * size throughout -- the offset gives way, not the artwork.
   *
   * The 170px floor is the worst-case hero overhang: "scratch dropday" on designs 3-8 is
   * 310x242 at translate(-50%, -67%), so 0.67 * 242 = 162px above the card edge. Below a
   * ~762px viewport the page scrolls a little rather than clipping the artwork.
   */
  padding-top: max(170px, min(27.5svh, calc(100svh - 592px)));

  @media ${({ theme }) => theme.breakpoints.lg} {
    padding-top: 16svh;
  }

  /*
   * Mobile clears the countdown by a FIXED gap, not a share of the viewport height.
   *
   * The hero graphics hang above the card's top edge by a fraction of the CARD WIDTH, so
   * the clearance they need tracks the viewport's width; an svh offset tracks its height.
   * The two drift apart, and on a short phone the artwork reached into the countdown.
   *
   * 0.588 is the worst of the three overhangs, all derived from the mobile assets:
   *   lucky 8s         343/343 wide, 224 tall, translateY -90%  ->  0.5878 x cardW
   *   scratch dropday  155/343 wide, 120 tall, translateY -97%  ->  0.3394 x cardW
   *   special prices    86/343 wide,  64 tall, translateY -80%  ->  0.1493 x cardW
   * Recompute it if an anchor's translateY or a mobile asset's dimensions change.
   *
   * The trailing -3rem is an offset on top of that clearance, so the artwork deliberately
   * rides up over the bottom of the countdown rather than clearing it. Because the overhang
   * term cancels against the graphics' own height, that overlap is a constant 3rem on every
   * phone and does not vary with the countdown's height.
   */
  @media ${({ theme }) => theme.breakpoints.sm} {
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 1.5rem;
    padding-top: calc(0.588 * (100vw - 2rem) - 3rem);
  }

  /*
   * Below lg the card IS allowed to shrink to fit -- unlike desktop, which holds the
   * Figma 792px. There is no tablet frame to violate, and from lg down the countdown
   * stops being position:fixed and takes real flow space, so something has to give.
   *
   * This is the tile model inverted: cardW = k * (cardH - chrome) + chrome, where
   * k = cols * ratio_w / (2 * ratio_h). The constant therefore changes with the tile
   * proportion, which changes at md when the title is dropped:
   *   above md  title shown, 171.5:193  ->  k = 4 * 171.5 / (2 * 193) = 1.7772
   *   below md  price only,  75:103     ->  k = 4 * 75    / (2 * 103) = 1.4563
   *
   * 271px is what sits above and below the card here that does not on desktop: 155px for
   * ScratchSection's 4.5rem header clearance plus the in-flow countdown, the same 100px
   * footer block, and 16px of slack. The 16svh is this element's own top offset.
   *
   * md must come after lg: both are max-width queries of equal specificity, so source
   * order is what makes the narrower one win.
   */
  @media ${({ theme }) => theme.breakpoints.lg} {
    --card-w-fit: max(
      20rem,
      calc(1.7772 * (100svh - 16svh - 271px - 106px) + 106px)
    );
  }

  @media ${({ theme }) => theme.breakpoints.md} {
    --card-w-fit: max(
      20rem,
      calc(1.4563 * (100svh - 16svh - 271px - 106px) + 106px)
    );
  }
`;

/**
 * Both footer lines carry a solid black outline, as in the design — without it they
 * disappear into the lighter patches of the camo.
 *
 * `paint-order: stroke fill` is the load-bearing half. `-webkit-text-stroke` alone centres
 * the stroke on the glyph edge, so half of it eats INWARDS and the letters come out visibly
 * thinner — very obvious on a condensed face like Anton. Painting the stroke first and the
 * fill over it keeps the glyph at full weight and puts the whole 1px outside.
 */
export const ScratchFooterNote = styled.p`
  font-family:
    ${fontFamilies.Anton}, 'Arial Narrow', 'Haettenschweiler', sans-serif;
  font-size: 1.125rem;
  /*
   * Pinned. Anton's default line box is ~1.45em, and the 592px constant in ScratchInner
   * budgets these two lines to the pixel -- with line-height: normal that budget is a
   * guess that also shifts during the font swap, when Arial Narrow stands in.
   */
  line-height: 1.2;
  margin: 0;
  text-align: center;
  text-transform: uppercase;
  color: var(--scratch-footer-primary, #ffffff);
  paint-order: stroke fill;
  -webkit-text-stroke: 2px #000000;

  @media ${breakpoints.sm} {
    font-size: 0.875rem;
  }
`;

export const ScratchShippingNote = styled(ScratchFooterNote)`
  color: var(--scratch-footer-shipping, #fceb8a);
`;

/**
 * Half the graphic hangs outside the card on each anchored edge. The horizontal sign
 * flips for the right-hand anchor because it is offset from `right` rather than `left`.
 */
/*
 * Desktop and tablet placement. Every transform ends in rotate(var(--hero-rotate, 0deg)),
 * which is how a graphic opts into an extra tilt (ScratchHeroArt.rotation) without each
 * anchor needing a variant. Unset means 0deg, so the other two are unaffected.
 *
 * ANCHOR_POSITION_SM deliberately has no rotate term: it overwrites transform wholesale at
 * sm, so mobile cannot inherit a rotation from here.
 */
const ANCHOR_POSITION: Record<ScratchHeroAnchor, ReturnType<typeof css>> = {
  topLeft: css`
    top: 0;
    left: 0;
    transform: translate(-50%, -50%) rotate(var(--hero-rotate, 0deg));
  `,
  // Dropday sits higher above the card than a half-overhang would put it; Lucky 8s, the
  // tallest of the three, sits lower. The asymmetry is deliberate.
  top: css`
    top: 0;
    left: 50%;
    transform: translate(-50%, -67%) rotate(var(--hero-rotate, 0deg));
  `,
  topRight: css`
    top: 0;
    right: 0;
    transform: translate(50%, -35%) rotate(var(--hero-rotate, 0deg));
  `,
};

/**
 * Mobile keeps the same anchor points but hangs the artwork out far less horizontally and
 * lifts it further above the card instead.
 *
 * At 375px the card is nearly the full viewport, so a 50% horizontal overhang would put half
 * of each graphic off-screen — which is exactly what it did on the first attempt. The design
 * stacks them ABOVE the card there rather than beside it.
 */
/*
 * Mobile placement. NO anchor here may carry a rotate(): the mobile artwork ships as its own
 * set of assets with the design's rotation and crop already baked into the pixels, which is
 * what makes these pure translations. Matching the mobile angles with CSS transforms on the
 * desktop assets was tried and could not be landed by eye.
 */
const ANCHOR_POSITION_SM: Record<ScratchHeroAnchor, ReturnType<typeof css>> = {
  // Scratch dropday: centre on desktop, left on mobile.
  topLeft: css`
    top: 0;
    left: 0;
    right: auto;
    transform: translate(-14%, -97%);
  `,
  // Lucky 8s: the mobile layout moves it from the card's top-right to the middle.
  top: css`
    top: 0;
    left: 50%;
    right: auto;
    transform: translate(-50%, -90%);
  `,
  // Special prices: left on desktop, right on mobile.
  topRight: css`
    top: 0;
    left: auto;
    right: 0;
    transform: translate(0%, -80%);
  `,
};

/**
 * A hero graphic hanging off the card.
 *
 * Rendered INSIDE the card so it is positioned by the thing it frames. Its centre sits on
 * a corner or edge midpoint of the card, so exactly half hangs outside -- that is what the
 * translate does, and it needs no measured coordinates at all.
 *
 * Only the WIDTH and ROTATION vary per graphic, and they arrive as custom properties so a
 * media query can swap both at once without a second element (a display:none image is
 * still downloaded by most browsers).
 */
export const ScratchHeroImage = styled.img<{
  $anchor: ScratchHeroAnchor;
  $mobileAnchor: ScratchHeroAnchor;
  $mobileAboveCard: boolean;
}>`
  position: absolute;
  z-index: 3;
  height: auto;
  width: var(--hero-w);
  pointer-events: none;

  ${({ $anchor }) => ANCHOR_POSITION[$anchor]}

  @media ${({ theme }) => theme.breakpoints.sm} {
    width: var(--hero-w-sm);
    /*
     * Behind the ENTIRE card on mobile -- border, surface, products and badge -- because
     * the graphics are large relative to a 375px card and would otherwise bury the first
     * row of products. Above sm they all stay in front, which is what the design shows.
     *
     * The card sits at z-index 1, so a graphic flagged mobileAboveCard goes to 2.
     */
    z-index: ${({ $mobileAboveCard }) => ($mobileAboveCard ? 2 : 0)};
    ${({ $mobileAnchor }) => ANCHOR_POSITION_SM[$mobileAnchor]}
  }
`;

/**
 * Wraps the card and the hero artwork as SIBLINGS, and owns the card's width.
 *
 * The artwork has to be layerable against the whole card, border included -- and an
 * element's own border and background are painted before ALL of its positioned children,
 * whatever z-index those children carry. So while the artwork lived INSIDE the card, no
 * z-index could ever put the card's 4px border or its surface in front of it; only the
 * card's content could be lifted, which left the border and texture stranded behind.
 *
 * As siblings both are ordinary positioned boxes and one z-index settles it:
 *
 *   hero art   z-index 3 above sm, 0 at sm
 *   the card   z-index 1
 *
 * The frame carries the width so its padding box is exactly the card's border box, which
 * keeps the anchors and the percentage widths meaning the same thing as before.
 */
export const ScratchCardFrame = styled.div`
  position: relative;
  z-index: 3;
  width: 100%;
  /*
   * 49.5rem = 792px, the 952px Figma column minus its 80px side padding.
   *
   * The calc(100% - 16rem) term reserves 8rem of gutter on each side so the hero graphics,
   * which hang half outside the card, always have somewhere to hang. Without it the card
   * stayed 792px wide as the viewport narrowed and the artwork was clipped by the screen
   * edge from roughly 1050px down. The two terms cross over at ~1048px viewport, above
   * which the 49.5rem cap wins, so desktop is untouched.
   */
  max-width: min(49.5rem, calc(100% - 16rem));

  /* Tablet only -- see --card-w-fit on ScratchInner. Desktop keeps the design size. */
  @media ${({ theme }) => theme.breakpoints.lg} {
    max-width: min(49.5rem, calc(100% - 16rem), var(--card-w-fit));
  }

  /*
   * Full bleed less a 16px gutter each side. ScratchInner already supplies exactly that
   * padding at sm, so width: 100% resolves to it and the cap just has to get out of the
   * way -- no second place to keep the 16px in sync.
   *
   * This supersedes the flat 335px Figma frame (node 463:991), which was 20px of margin
   * each side on a 375px screen. The tile therefore grows with the phone rather than
   * being pinned: 76.8px at 375px wide against the design's 75px, and proportionally more
   * on a larger handset. The hero widths are percentages of the card, so they scale with
   * it and the composition holds together.
   */
  @media ${({ theme }) => theme.breakpoints.sm} {
    max-width: none;
  }
`;

/* ------------------------------------------------------------------ the card */

/**
 * The "tablet": a scratch-card slab holding the product grid.
 *
 * Figma node 463:34 — 4px #321221 outer border, an inner 1px #b42a20 border, and a paper
 * surface. The surface was three layers in the design (white, then two textures at 40% and
 * 20% opacity); they are pre-composited into one opaque `card-surface.webp` offline, which
 * turned 1.18MB and two runtime opacity layers into 42KB and none.
 */
export const ScratchCardOuter = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  /*
   * 49.5rem = 792px, the 952px Figma column minus its 80px side padding.
   *
   * The calc(100% - 16rem) term reserves 8rem of gutter on each side so the hero
   * graphics, which hang half outside this box, always have somewhere to hang. Without it
   * the card stayed 792px wide as the viewport narrowed and the artwork was clipped by the
   * screen edge from roughly 1050px down.
   *
   * Desktop is untouched: the two terms cross over at ~1048px viewport, above which the
   * 49.5rem cap wins. Below sm the card goes full width and the mobile anchors take over,
   * which lift the artwork above the card instead of pushing it out sideways.
   */
  border: 4px solid #321221;
  /* The hero graphics hang half outside this box; clipping them is the one thing that
     would break the whole composition. */
  overflow: visible;
  background-color: #d9d9d9;
  background-image: var(--scratch-card-surface);
  background-size: cover;
  background-position: center;

  @media ${({ theme }) => theme.breakpoints.sm} {
    border-width: 3px;
  }
`;

export const ScratchCardInner = styled.div`
  border: 1px solid #b42a20;
  padding: 1.5rem;

  /*
   * Asymmetric on mobile, and both halves of that are load-bearing.
   *
   * SIDES are 0.5rem, not 0.75rem: the Figma mobile tile is 75px wide across a 335px card,
   * which leaves only 35px for the whole horizontal chrome -- 8px of borders (3px outer +
   * 1px inner, both sides) and 27px of padding. With 12px here and 12px on the panel the
   * chrome came to 56px and the tile to 69.75px, 7% under the design.
   *
   * TOP and BOTTOM are free of that constraint -- only the horizontal padding is fixed by
   * the tile width, so both verticals come straight from Figma: 1rem above, 1.5rem below.
   * The larger bottom value clears the brokenplanet badge, which is absolutely positioned
   * on the card's bottom border and overlapped the last row of products when the sides'
   * 0.5rem was applied all round.
   */
  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 1rem 0.5rem 1.5rem;
  }
`;

/**
 * `container-type: inline-size` so the grid can size its rows from the panel's own width
 * (`cqi`) rather than the viewport. That is what keeps exactly two rows visible at every
 * width without a hardcoded height per breakpoint.
 */
export const ScratchPanel = styled.div`
  /*
   * The containing block for the scratch-off overlay, which is inset: 0 against this box so
   * the coating covers the product grid and nothing else.
   *
   * container-type below already implies layout containment, which on its own makes this a
   * containing block for absolutely positioned descendants -- position: relative is stated
   * anyway so the relationship survives someone removing the container query.
   */
  position: relative;
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1.5rem;
  border-radius: 20px;
  background: rgba(49, 49, 47, 0.2);

  /* The other half of the 36px mobile chrome -- see ScratchCardInner. */
  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 0.375rem;
    border-radius: 12px;
  }
`;

/**
 * Exactly two rows are visible; anything beyond eight products scrolls INSIDE the card, so
 * the slab keeps its height and the page never grows. Row height is derived from the tile
 * aspect ratio and the panel's own inline size, so it holds at every width.
 *
 * NOTE the scrollbar is invisible: `app/styles/app.css` hides every scrollbar globally with
 * `::-webkit-scrollbar { display: none !important }`. The fade at the bottom edge is
 * therefore the ONLY signal that more products exist — without it a ninth item is
 * undiscoverable on a timed drop.
 */
export const ScratchScroll = styled.div`
  --cols: 4;

  /*
   * No gutter, which is what the design says: the Figma desktop tile is 171.5px wide and
   * the panel is 686px (792 card - 106 chrome), and 686 / 4 = 171.5 exactly. Kept as a
   * variable so a gutter can be added without unpicking the arithmetic.
   */
  --gap: 0rem;
  --tile-w: calc((100cqi - (var(--cols) - 1) * var(--gap)) / var(--cols));

  /*
   * The Figma desktop tile is 171.5 x 193, and the title + price block rides INSIDE that
   * 193 -- it is not added on top of it. The previous 215/171.5 did add it on top, which
   * is where 44px of surplus card height came from and why the page needed an ~890px
   * viewport to fit. At the 792px card this now resolves to exactly 171.5 x 193.
   */
  --tile-h: calc(var(--tile-w) * 193 / 171.5);

  height: calc(var(--tile-h) * 2 + var(--gap));
  overflow-y: auto;
  overscroll-behavior: contain;

  /*
   * Firefox never sees the global ::-webkit-scrollbar rule in app/styles/app.css, so past
   * eight products it would take ~15px out of this element's width while --tile-w is
   * computed from 100cqi on the unshrunk ScratchPanel. Same declaration as ShopFilters
   * and ThumbnailGallery.
   */
  scrollbar-width: none;

  /*
   * Tablet borrows the MOBILE tile, not the desktop one, because the title is hidden from
   * md down exactly as it is on a phone -- so the label block is the price alone and the
   * desktop 171.5:193 proportion no longer describes it. The Figma mobile tile is 75 x 103.
   *
   * This tracks TileTitle's own breakpoint and must keep tracking it: show a title in a
   * tile sized for a price-only label and the two lines eat the image box instead.
   *
   * This replaces a formula that scaled the whole MOBILE CARD's 335:309 proportion across
   * the entire 601-1280px band with no reference to tile width at all: crossing 1280px the
   * row jumped 207.5px to 308px while the tile stayed 165.5px wide, and because
   * TileImageWrap absorbs surplus row height, object-fit: cover then cropped the sides.
   */
  @media ${({ theme }) => theme.breakpoints.md} {
    --tile-h: calc(var(--tile-w) * 103 / 75);
  }
`;

export const ScratchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(var(--cols), 1fr);
  gap: var(--gap);
  grid-auto-rows: var(--tile-h);
`;

/**
 * The `brokenplanet` badge straddling the card's bottom edge (node 463:114).
 * `translateY(50%)` puts its centre on the border, as drawn.
 */
/**
 * The brokenplanet badge on the card's bottom edge (node 463:114).
 *
 * translateY is 5%, not 50%: the badge sits ON the bottom border with only a sliver below
 * it, rather than straddling the edge with half its height hanging outside.
 *
 * Rendered as a CSS MASK rather than an <img>, because the wordmark is byte-identical
 * across designs apart from its fill -- gold #EFCA48 on design 1, cyan #6BC4D6 on
 * design 2. One asset tinted from the palette beats eight near-duplicate SVGs, and it
 * keeps the colour beside the other per-design colours instead of hidden inside a file.
 */
export const ScratchCardBadge = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 5%);
  display: flex;
  align-items: center;
  justify-content: center;
  background: #321221;
`;

/**
 * Carries the wordmark's glow, recovered from the artwork it used to be baked into:
 * stdDeviation 4 in a 224-unit viewBox at 50% opacity, with no offset.
 *
 * A SEPARATE ELEMENT because CSS applies filter BEFORE mask on the same element. A
 * drop-shadow on ScratchCardBadgeMark would be generated from its unmasked background box
 * and then clipped away by its own mask, leaving a glow only INSIDE the glyphs. Put on an
 * ancestor, the filter sees the already-masked wordmark and haloes it properly.
 *
 * The colour is its OWN palette entry, not the accent: Figma gives six of the eight
 * designs a lighter tint for the glow than for the wordmark, so it cannot be derived. The
 * asset this replaced had #fceb8a baked in for every design, so 2 to 8 all glowed design
 * 1's gold.
 *
 * The radius is in px because filter blur takes no percentages, so it is restated per
 * breakpoint to track the badge's two widths. Figma specifies blur 8 against the artwork's
 * 224-unit box; the badge renders that box at 208px on desktop and 120px below lg, giving
 * 7.4px and 4.3px. (Figma's blur maps 1:1 onto the CSS blur-radius -- the old asset's SVG
 * stdDeviation was 4, and CSS blur-radius is twice the Gaussian stdDeviation.)
 */
export const ScratchCardBadgeGlow = styled.span`
  display: flex;
  filter: drop-shadow(0 0 7.4px var(--scratch-glow));

  @media ${({ theme }) => theme.breakpoints.lg} {
    filter: drop-shadow(0 0 4.3px var(--scratch-glow));
  }
`;

export const ScratchCardBadgeMark = styled.span`
  display: block;
  width: 13rem;
  /*
   * The box keeps the 224x40 proportions of the artwork this badge was cut from, even
   * though the mask is now the header's 208x24 wordmark. Those are the same logo: the old
   * scratch-specific file was this wordmark inset 8px inside a 224x40 box, the padding
   * existing only to hold a Figma drop shadow.
   *
   * The padding held a glow, which is now applied in CSS by ScratchCardBadgeGlow instead.
   * Moving it out of the asset also fixes it: the baked version was #fceb8a at 50%, design
   * 1's gold, hardcoded -- so designs 2 to 8 glowed the wrong colour. In CSS it tints from
   * the palette like everything else on this badge.
   *
   * Sizing the mask to 92.857% (208/224) and centring it reproduces the old 8px inset
   * exactly, so the wordmark lands at precisely the size and position it did before.
   */
  aspect-ratio: 224 / 40;
  background-color: var(--scratch-badge);
  -webkit-mask-image: var(--scratch-badge-mask);
  mask-image: var(--scratch-badge-mask);
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: 92.857% auto;
  mask-size: 92.857% auto;
  -webkit-mask-position: center;
  mask-position: center;

  /*
   * The mobile size from tablet down, not just on phones. This width is fixed while the
   * card shrinks to fit the fold below lg, so 13rem goes from 26% of a 792px card to
   * roughly 40% of a squeezed one and reads as oversized.
   */
  @media ${({ theme }) => theme.breakpoints.lg} {
    width: 7.5rem;
  }
`;

/* ------------------------------------------------------------------ the tile */

export const TileLink = styled(Link)`
  display: flex;
  overflow: hidden;
  flex-direction: column;
  min-width: 0;
  text-decoration: none;
`;

// Dims the image only, so the badge over it keeps full contrast. Same 0.5 as
// ProductCard's ImageWrapper.
export const TileImageWrap = styled.div<{ $soldOut?: boolean }>`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;

  > img {
    opacity: ${({ $soldOut }) => ($soldOut ? 0.5 : 1)};
  }
`;

export const TileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const TileTitle = styled.span`
  display: block;
  /*
   * overflow: hidden below (for the line clamp) sets this item's automatic minimum size
   * to zero, so without flex: none it is the first thing a column flex row shrinks --
   * silently stealing height from itself rather than from the image box.
   */
  flex: none;
  padding: 0 0.5rem;
  text-align: center;
  text-transform: uppercase;
  font-family: ${fontFamilies.CircularStd};
  font-size: 0.75rem;
  letter-spacing: 0.0094rem;
  color: ${({ theme }) => theme.colors.white};
  /* Figma wraps the title to two lines rather than truncating; a clamp keeps the tile
     height predictable while still showing more than an ellipsis would. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.15;
  /* Reserve both lines whether or not the title needs them, so every tile in a row
     ends its image at the same height. */
  min-height: 2.3em;

  /*
   * Dropped from tablet down. md (960px) rather than lg (1280px): lg is where this app
   * switches to the hamburger and drops the sidebar, but plenty of laptops report 1280
   * CSS pixels or fewer once display scaling is applied, and those were losing product
   * names they have room for.
   *
   * ScratchScroll's tile proportion is keyed to this same breakpoint -- the two must move
   * together, because the tile below md is sized for a price-only label.
   */
  @media ${({ theme }) => theme.breakpoints.md} {
    display: none;
  }
`;

export const TilePrice = styled.span`
  display: block;
  flex: none;
  line-height: 1.2;
  padding: 0 0.5rem 2px;
  text-align: center;
  text-transform: uppercase;
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 0.75rem;
  letter-spacing: 0.0313rem;
  color: ${({ theme }) => theme.colors.white};
`;

/**
 * The metallic treatment shared by both lines of the free-item badge.
 *
 * Two separate mechanisms that cannot be collapsed into one property: the fill arrives as a
 * BACKGROUND clipped to the glyphs, so the text itself must be transparent, while the
 * outline is a text stroke. paint-order: stroke fill puts the whole stroke OUTSIDE the
 * glyph -- webkit-text-stroke alone centres it on the edge, so half eats inwards and a
 * condensed face like Anton comes out visibly thinner.
 *
 * The forced-colors block is not optional. background-clip: text over a transparent fill
 * renders NOTHING under Windows High Contrast, so without it the badge simply vanishes for
 * those users and a free product looks like any other. This is the specific trap that made
 * this badge artwork in the first place; going back to text means carrying the fallback.
 */
const freeBadgeInk = css`
  /*
   * The font belongs HERE, on the element that holds the text, and not on the flex parent.
   * app/styles/app.css declares "* { font-family: 'CircularStd', sans-serif }", which sets
   * the family DIRECTLY on every element in the document -- and a direct declaration always
   * beats an inherited one, whatever the parent says. Anything relying on font inheritance
   * in this codebase silently renders in CircularStd.
   */
  font-family:
    ${fontFamilies.Anton}, 'Arial Narrow', 'Haettenschweiler', sans-serif;

  background: linear-gradient(180deg, #d9dadc 30%, #5b5a5b 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  paint-order: stroke fill;
  -webkit-text-stroke: 1px #321221;

  @media (forced-colors: active) {
    background: none;
    color: CanvasText;
    -webkit-text-fill-color: currentColor;
    -webkit-text-stroke-width: 0;
  }
`;

/**
 * "FREE / WITH PURCHASE" over the product image (node 463:88).
 *
 * Live text rather than the artwork it used to be, so it stays crisp at any tile size and
 * is announced to screen readers -- the price row reads "0.00", which does not say free.
 *
 * The two lines are sized in absolute px from the design rather than scaled from the tile.
 * Anton is loaded with font-display: swap, so during the swap Arial Narrow stands in at
 * different metrics; the container is centred on both axes rather than laid out from a
 * corner, so a reflow moves both lines symmetrically instead of shifting the block.
 */
export const TileFreeBadge = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  pointer-events: none;
  text-align: center;
  /* text-transform DOES inherit usefully -- app.css sets no value for it, so there is no
     direct declaration on the children to beat it. Unlike font-family; see freeBadgeInk. */
  text-transform: uppercase;
`;

/** "FREE" -- 32px desktop, 16px from md down, line-height locked to the font size. */
export const TileFreeBadgeLead = styled.span`
  ${freeBadgeInk}
  font-size: 2rem;
  line-height: 2rem;

  /*
   * The mobile size applies from md, not sm. Below md this component is already in its
   * mobile shape -- the title is hidden and the tile takes the 75:103 mobile proportion --
   * and a 32px word does not fit the ~81px tile that band produces.
   */
  @media ${({ theme }) => theme.breakpoints.md} {
    font-size: 1rem;
    line-height: 1rem;
    -webkit-text-stroke-width: 0.5px;
  }
`;

/** "WITH PURCHASE" -- 14px desktop, 8px from md down, line-height auto per the design. */
export const TileFreeBadgeSub = styled.span`
  ${freeBadgeInk}
  font-size: 0.875rem;
  line-height: normal;

  @media ${({ theme }) => theme.breakpoints.md} {
    font-size: 0.5rem;
    /* A 1px stroke on an 8px glyph closes the counters and the gradient stops reading. */
    -webkit-text-stroke-width: 0.5px;
  }
`;

/** The small "8" that precedes the drops line (node 463:119). Shared across designs. */
/** "SOLD OUT" over a sold-out tile, in the free badge's ink so the two read as one family.
    Sized between the free badge's two lines: the lead's 32px does not fit the word on a
    165px tile. */
export const TileSoldOutBadge = styled.span`
  ${freeBadgeInk}
  font-size: 1.25rem;
  line-height: 1.25rem;

  @media ${({ theme }) => theme.breakpoints.md} {
    font-size: 0.625rem;
    line-height: 0.625rem;
    -webkit-text-stroke-width: 0.5px;
  }
`;

export const ScratchFooterEight = styled.img`
  display: inline-block;
  width: 1.75rem;
  height: auto;
  vertical-align: -0.2em;
  margin-right: 0.25rem;

  @media ${({ theme }) => theme.breakpoints.sm} {
    width: 1.25rem;
  }
`;
